import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import usePageTitle from "../hooks/usePageTitle";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

const RegisterPage = () => {
  usePageTitle('Create Account', 'Sign up for Advanta Plus membership');
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    terms_accepted: false,
    gdpr_accepted: false,
    marketing_accepted: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.terms_accepted) {
      toast.error("Please accept the Privacy Policy");
      return;
    }
    
    if (!formData.gdpr_accepted) {
      toast.error("Please accept the GDPR data processing terms");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData);
      toast.success("Account created successfully!");
      navigate("/membership");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1654363539422-76c4e6b93b3d?w=1920')", 
          backgroundSize: "cover", 
          backgroundPosition: "center" 
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#020617] via-[#020617]/90 to-[#020617]" />
      
      {/* Back Button */}
      <Link 
        to="/"
        className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors z-10"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>{t('common.back')}</span>
      </Link>
      
      {/* Form Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="glass-dark rounded-2xl p-8 shadow-luxury">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
                <span className="text-[#020617] font-bold text-xl tracking-tight">A+</span>
              </div>
            </Link>
            <h1 className="font-['Playfair_Display'] text-3xl text-white mt-4">{t('auth.create_account')}</h1>
            <p className="text-white/50 mt-2">{t('auth.join_advanta')}</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-white/80">{t('auth.first_name')}</Label>
                <Input
                  id="first_name"
                  type="text"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                  data-testid="register-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-white/80">{t('auth.last_name')}</Label>
                <Input
                  id="last_name"
                  type="text"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                  data-testid="register-lastname-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                data-testid="register-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => setFormData({ ...formData, terms_accepted: checked })}
                className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-1"
                data-testid="register-terms-checkbox"
              />
              <Label htmlFor="terms" className="text-white/60 text-sm leading-relaxed cursor-pointer">
                I accept the <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms & Conditions</Link> and <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link> *
              </Label>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="gdpr"
                checked={formData.gdpr_accepted}
                onCheckedChange={(checked) => setFormData({ ...formData, gdpr_accepted: checked })}
                className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-1"
                data-testid="register-gdpr-checkbox"
              />
              <Label htmlFor="gdpr" className="text-white/60 text-sm leading-relaxed cursor-pointer">
                I consent to the processing of my personal data in accordance with GDPR regulations *
              </Label>
            </div>
            
            <div className="flex items-start gap-3">
              <Checkbox
                id="marketing"
                checked={formData.marketing_accepted}
                onCheckedChange={(checked) => setFormData({ ...formData, marketing_accepted: checked })}
                className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-1"
                data-testid="register-marketing-checkbox"
              />
              <Label htmlFor="marketing" className="text-white/60 text-sm leading-relaxed cursor-pointer">
                I would like to receive promotional emails and special offers (optional)
              </Label>
            </div>
            
            <Button 
              type="submit" 
              className="btn-gold w-full py-6 rounded text-base"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t('common.loading') : t('auth.create_account')}
            </Button>
          </form>
          
          <div className="mt-8 text-center">
            <p className="text-white/50">
              {t('auth.have_account')}{" "}
              <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="login-link">
                {t('auth.sign_in_link')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
